/**
 * Seção Cenário AVCB do Relatório
 * Year rows com dados históricos fixos (2022-2024), dados dinâmicos (2025) e forecast
 */
import { cn } from '@/lib/utils';
import { Shield } from 'lucide-react';
import { useCenarioAvcb, useSnapshots, type CenarioResponse } from '../hooks';
import { YearRow, RiscosBox } from './YearRow';

// Flag de rollback: oculta a lista de snapshots mensais sem remover o servico,
// o cron ou os endpoints. Reative alterando para true.
const SHOW_SNAPSHOTS_MENSAIS = false;

// Referencia visual do forecast de AVCB (nao altera totais nem regras de classificacao)
const AVCB_FORECAST_REFERENCE_LABEL = 'Meta: 1.820';

// ============================================================================
// DADOS HISTÓRICOS FIXOS - Extraídos do backup
// ============================================================================

const HISTORICAL_DATA_AVCB: Record<number, CenarioResponse> = {
  2022: {
    regular: 639,
    regularPercent: 19,
    andamento: 84,
    andamentoPercent: 3,
    pendente: 2603,
    pendentePercent: 78,
    total: 3326,
    regularAndamento: 723,
    regularAndamentoPercent: 22,
  },
  2023: {
    regular: 1001,
    regularPercent: 33,
    andamento: 120,
    andamentoPercent: 4,
    pendente: 1932,
    pendentePercent: 63,
    total: 3053,
    regularAndamento: 1121,
    regularAndamentoPercent: 37,
  },
  2024: {
    regular: 1469,
    regularPercent: 48,
    andamento: 462,
    andamentoPercent: 15,
    pendente: 1105,
    pendentePercent: 37,
    total: 3036,
    regularAndamento: 1931,
    regularAndamentoPercent: 63,
  },
  2025: {
    regular: 1752,
    regularPercent: 59,
    andamento: 406,
    andamentoPercent: 14,
    pendente: 788,
    pendentePercent: 27,
    total: 2946,
    regularAndamento: 2158,
    regularAndamentoPercent: 73,
  },
};

const FORECAST_2026_AVCB: CenarioResponse = {
  regular: 1905,
  regularPercent: 66,
  andamento: 406,
  andamentoPercent: 14,
  pendente: 564,
  pendentePercent: 20,
  total: 2875,
  regularAndamento: 2311,
  regularAndamentoPercent: 80,
};

interface CenarioAvcbSectionProps {
  className?: string;
}

export function CenarioAvcbSection({ className }: CenarioAvcbSectionProps) {
  // Dados atuais (dinâmico)
  const { data: currentData, isLoading: loadingCurrent } = useCenarioAvcb();
  
  // Lista de snapshots disponíveis
  const { data: snapshots } = useSnapshots();

  // Agrupa snapshots por ano
  const snapshotsByYear = new Map<number, { month: number; year: number }[]>();
  if (snapshots) {
    for (const snap of snapshots) {
      const arr = snapshotsByYear.get(snap.year) || [];
      arr.push({ month: snap.month, year: snap.year });
      snapshotsByYear.set(snap.year, arr);
    }
  }

  return (
    <section id="cenario-avcb" className={cn('scroll-mt-20', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-red-500" />
        <div>
          <h2 className="text-2xl font-bold">Cenário AVCB</h2>
          <p className="text-sm text-muted-foreground">
            Evolução anual da conformidade de Auto de Vistoria do Corpo de Bombeiros
          </p>
        </div>
      </div>

      {/* Layout principal */}
      <div className="flex gap-6">
        {/* Riscos (sidebar) */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <RiscosBox type="avcb" className="sticky top-24" />
        </div>

        {/* Year Rows */}
        <div className="flex-1 space-y-4">
          {/* 2022 - Dados fixos */}
          <YearRow
            year={2022}
            data={HISTORICAL_DATA_AVCB[2022]}
            isLoading={false}
          />

          {/* 2023 - Dados fixos */}
          <YearRow
            year={2023}
            data={HISTORICAL_DATA_AVCB[2023]}
            isLoading={false}
          />

          {/* 2024 - Dados fixos */}
          <YearRow
            year={2024}
            data={HISTORICAL_DATA_AVCB[2024]}
            isLoading={false}
          />

          {/* 2025 - Dados fixos (fim do ano) */}
          <YearRow
            year={2025}
            data={HISTORICAL_DATA_AVCB[2025]}
            isLoading={false}
          />

          {/* 2026 - Dados dinâmicos (API) */}
          <YearRow
            year={2026}
            data={currentData || emptyData}
            isLoading={loadingCurrent}
          />

          {/* 2026 Forecast - Dados fixos */}
          <YearRow
            year={2026}
            isForecast
            referenceLabel={AVCB_FORECAST_REFERENCE_LABEL}
            data={FORECAST_2026_AVCB}
            isLoading={false}
          />

          {/* Snapshots mensais de 2026 (quando disponíveis) */}
          {SHOW_SNAPSHOTS_MENSAIS && snapshotsByYear.has(2026) && (
            <div className="mt-6">
              <div className="mb-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Snapshots Mensais 2026
                </span>
              </div>
              {snapshotsByYear.get(2026)!
                .sort((a, b) => b.month - a.month)
                .map(({ month, year: y }) => (
                  <SnapshotYearRow 
                    key={`${month}-${y}`}
                    month={month}
                    year={y}
                    type="avcb"
                  />
                ))
              }
            </div>
          )}
        </div>
      </div>

      {/* Riscos em mobile */}
      <div className="lg:hidden mt-6">
        <RiscosBox type="avcb" />
      </div>
    </section>
  );
}

// ============================================================================
// SNAPSHOT YEAR ROW
// ============================================================================

import { useCenarioAvcbHistorico } from '../hooks';

interface SnapshotYearRowProps {
  month: number;
  year: number;
  type: 'avcb' | 'alvara';
}

function SnapshotYearRow({ month, year, type }: SnapshotYearRowProps) {
  const hook = type === 'avcb' ? useCenarioAvcbHistorico : useCenarioAvcbHistorico;
  const { data, isLoading } = hook(month, year);

  return (
    <YearRow
      year={year}
      month={month}
      data={data || emptyData}
      isLoading={isLoading}
      className="opacity-90"
    />
  );
}

// Dados vazios para loading
const emptyData: CenarioResponse = {
  regular: 0,
  regularPercent: 0,
  andamento: 0,
  andamentoPercent: 0,
  pendente: 0,
  pendentePercent: 0,
  total: 0,
  regularAndamento: 0,
  regularAndamentoPercent: 0,
};
